// client/src/components/event/RafflePurchasePanel.jsx
//
// Lets a participant purchase raffle entries via the tenant's configured
// payment provider (Stripe or Square). Provider-agnostic — the server's
// /purchase-intent response tells us which checkout UI to render via
// <UnifiedCheckout/>.
//
// Each purchased ticket = one row in t_raffle_entry (server-side
// in raffleRoutes.finalizeRafflePurchase). The tenant's payment webhook
// is the safety net if the user closes the tab mid-flow.

import React, { useState, useMemo } from 'react';
import axios from 'axios';
import UnifiedCheckout from '../payment/UnifiedCheckout';

export default function RafflePurchasePanel({
  tenantId, eventId, itemId,
  pricePerEntry, maxPerPurchase,
  // Buyer details — passed in so we share state with the parent free form.
  buyer,          // { name, email, phone, attributes }
  attributesValid,
}) {
  const [numEntries, setNumEntries] = useState(1);
  // intent shape: {
  //   provider, clientSecret?, sdkConfig?, purchaseId, paymentIntentId?, total
  // }
  const [intent, setIntent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null); // { entries, pendingWebhook }

  const cap = Math.max(1, Number(maxPerPurchase) || 10);
  const total = useMemo(
    () => Math.round((Number(numEntries) || 0) * (Number(pricePerEntry) || 0) * 100) / 100,
    [numEntries, pricePerEntry]
  );
  const totalLabel = `$${total.toFixed(2)}`;

  const buyerValid = !!(buyer?.name && buyer?.email && attributesValid);

  const startPurchase = async () => {
    setError(null);
    if (!buyerValid) {
      setError('Please complete the name, email, and selection fields above first.');
      return;
    }
    const n = Math.max(1, Math.min(cap, Number(numEntries) || 1));
    setBusy(true);
    try {
      const baseUrl = process.env.REACT_APP_BASE_URL || '';
      const res = await axios.post(
        `${baseUrl}/api/raffle/${tenantId}/event/${eventId}/item/${itemId}/purchase-intent`,
        {
          name: buyer.name,
          email: buyer.email,
          phone: buyer.phone || null,
          attributes: buyer.attributes || {},
          num_entries: n,
        }
      );
      if (res.data?.success) {
        setIntent({
          provider: res.data.provider || 'stripe',
          clientSecret: res.data.clientSecret || null,
          sdkConfig: res.data.sdkConfig || null,
          paymentIntentId: res.data.paymentIntentId || null,
          purchaseId: res.data.purchaseId,
          total: res.data.total,
        });
      } else {
        setError(res.data?.message || 'Could not start checkout');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not start checkout');
    } finally {
      setBusy(false);
    }
  };

  // Called by UnifiedCheckout after the provider's client SDK does its part.
  // payload: { providerPaymentId? }  for Stripe
  //          { sourceToken? }        for Square
  const handleCheckoutSuccess = async (payload) => {
    const baseUrl = process.env.REACT_APP_BASE_URL || '';
    try {
      const res = await axios.post(
        `${baseUrl}/api/raffle/${tenantId}/event/${eventId}/item/${itemId}/purchase-confirm`,
        {
          purchaseId: intent.purchaseId,
          providerPaymentId: payload.providerPaymentId || null,
          sourceToken: payload.sourceToken || null,
        }
      );
      if (res.data?.success) {
        setSuccess({ entries: res.data.entries_added || numEntries, pendingWebhook: false });
      } else {
        setSuccess({ entries: numEntries, pendingWebhook: true });
      }
    } catch (postErr) {
      console.warn('purchase-confirm post failed; webhook fallback:', postErr?.message);
      setSuccess({ entries: numEntries, pendingWebhook: true });
    }
  };

  const reset = () => {
    setIntent(null);
    setSuccess(null);
    setError(null);
    setNumEntries(1);
  };

  if (success) {
    const msg = success.pendingWebhook
      ? `Payment succeeded — your ${success.entries || 'entries'} are being processed. Check your email shortly.`
      : `🎉 ${success.entries} ${success.entries === 1 ? 'entry' : 'entries'} confirmed — good luck!`;
    return (
      <div style={panelStyle}>
        <p style={{ color: '#10b981', fontWeight: 600 }}>{msg}</p>
        <button type="button" onClick={reset} style={secondaryButtonStyle}>Buy more entries</button>
      </div>
    );
  }

  return (
    <div style={panelStyle}>
      <h4 style={{ margin: '0 0 12px 0', color: '#fff' }}>Buy raffle entries</h4>
      <p style={{ margin: '0 0 12px 0', color: '#aaa', fontSize: 14 }}>
        Each ticket is one chance to win — ${Number(pricePerEntry).toFixed(2)} per entry.
      </p>

      {!intent && (
        <>
          <div className="form-group">
            <label htmlFor="num_entries">Number of entries</label>
            <input
              id="num_entries"
              type="number"
              min="1"
              max={cap}
              step="1"
              value={numEntries}
              onChange={(e) => {
                const v = Math.max(1, Math.min(cap, Number(e.target.value) || 1));
                setNumEntries(v);
              }}
              disabled={busy}
            />
            <small className="form-hint">Max {cap} per purchase.</small>
          </div>

          <div style={{ margin: '12px 0', fontSize: 18, color: '#fff' }}>
            Total: <strong style={{ color: '#ff6800' }}>{totalLabel}</strong>
          </div>

          {error && <div style={{ color: '#dc2626', marginBottom: 12, fontSize: 14 }}>{error}</div>}

          <button
            type="button"
            onClick={startPurchase}
            disabled={busy || !buyerValid}
            style={{
              ...primaryButtonStyle,
              opacity: (busy || !buyerValid) ? 0.6 : 1,
              cursor: (busy || !buyerValid) ? 'not-allowed' : 'pointer',
            }}
          >
            {busy ? 'Starting…' : `Checkout — ${totalLabel}`}
          </button>
          {!buyerValid && (
            <p style={{ marginTop: 8, fontSize: 12, color: '#aaa' }}>
              Complete name, email, and selection above to enable checkout.
            </p>
          )}
        </>
      )}

      {intent && (
        <>
          <UnifiedCheckout
            provider={intent.provider}
            clientSecret={intent.clientSecret}
            sdkConfig={intent.sdkConfig}
            amountLabel={`$${Number(intent.total).toFixed(2)}`}
            onSuccess={handleCheckoutSuccess}
            onError={(msg) => setError(msg)}
            onCancel={reset}
          />
          {error && <div style={{ color: '#dc2626', marginTop: 12, fontSize: 14 }}>{error}</div>}
        </>
      )}
    </div>
  );
}

const panelStyle = {
  marginTop: 16,
  padding: 16,
  border: '1px solid #333',
  borderRadius: 8,
  backgroundColor: '#0f0f1a',
};

const primaryButtonStyle = {
  width: '100%',
  padding: '12px 16px',
  backgroundColor: '#ff6800',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: 16,
  fontWeight: 600,
};

const secondaryButtonStyle = {
  width: '100%',
  padding: '10px 16px',
  backgroundColor: 'transparent',
  color: '#aaa',
  border: '1px solid #444',
  borderRadius: 6,
  fontSize: 14,
  cursor: 'pointer',
};
